import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, DollarSign, LineChart, BarChart3, TrendingUp, Calculator } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { BestDealBanner } from '@/components/BestDealBanner';
import { FeaturedFirms } from '@/components/FeaturedFirms';
import { EmailSubscribe } from '@/components/EmailSubscribe';
import { PayoutProof } from '@/components/PayoutProof';
import { blogPosts } from '@/data/blogPosts';
import { Helmet } from 'react-helmet-async';
import { organizationSchema } from '@/lib/seo';
import { getABVariant, AB_TESTS } from '@/utils/abtest';
import { InternalLinkModule } from '@/components/InternalLinkModule';

const Index = () => {
  const hpVariant = getABVariant(AB_TESTS.HOMEPAGE_CTA);

  const heroCta = hpVariant === 'A' ? 'Compare All Firms' : 'Find Your Perfect Firm';
  const midCta = hpVariant === 'A' ? 'Compare All Firms' : 'See All Deals';
  const howItWorksCta = hpVariant === 'A' ? 'Start Comparing' : 'Find Your Firm Now';

  return (
    <>
      <Helmet>
        <title>PinnacleBuy | Compare Futures Prop Trading Firms</title>
        <meta name="description" content="Compare the best futures prop trading firms side by side. Find exclusive discount codes, compare pricing, drawdown rules, and profit splits — updated regularly." />
        <link rel="canonical" href="https://pinnaclebuy.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="PinnacleBuy | Compare Futures Prop Trading Firms" />
        <meta property="og:description" content="Compare the best futures prop trading firms side by side. Find exclusive discount codes and the lowest prices." />
        <meta property="og:image" content="https://pinnaclebuy.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PinnacleBuy | Compare Futures Prop Trading Firms" />
        <meta name="twitter:description" content="Compare the best futures prop trading firms side by side. Find exclusive discount codes and the lowest prices." />
        <meta name="twitter:image" content="https://pinnaclebuy.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema())}</script>
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="relative bg-gradient-to-br from-brand-blue via-blue-600 to-indigo-700 text-white overflow-hidden">
            {/* Background Decorative Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {/* Trading Chart Decorative Elements */}
              <div className="absolute inset-0 opacity-40">
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1200 400" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(96, 165, 250)" stopOpacity="0.1" />
                      <stop offset="100%" stopColor="rgb(96, 165, 250)" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  
                  <g className="animate-float-very-slow">
                    {/* Background Volume Bars */}
                    <g className="opacity-10">
                      {[...Array(40)].map((_, i) => (
                        <rect 
                          key={i} 
                          x={i * 30} 
                          y={280 - (Math.random() * 100)} 
                          width="15" 
                          height={120 + (Math.random() * 100)} 
                          className="fill-blue-300/20" 
                        />
                      ))}
                    </g>

                    {/* Area Chart */}
                    <path 
                      d="M0,300 C150,280 300,260 450,220 C600,180 750,160 900,140 C1050,120 1200,100 1200,100 L1200,400 L0,400 Z" 
                      fill="url(#areaGradient)"
                    />

                    {/* Main Trend Line */}
                    <path 
                      d="M0,300 C150,280 300,260 450,220 C600,180 750,160 900,140 C1050,120 1200,100 1200,100" 
                      className="stroke-blue-300/40" 
                      strokeWidth="1.5" 
                      fill="none"
                    />

                    {/* Multiple Layers of Candlesticks */}
                    {/* Layer 1 - Background */}
                    <g className="animate-float-subtle opacity-20">
                      {[...Array(15)].map((_, i) => {
                        const x = i * 80;
                        const y = 280 - (i * 10);
                        return (
                          <g key={`bg-${i}`}>
                            <rect x={x} y={y} width="20" height="40" className="fill-blue-300/30" />
                            <line x1={x + 10} y1={y - 20} x2={x + 10} y2={y} className="stroke-blue-300/30" strokeWidth="2" />
                            <line x1={x + 10} y1={y + 40} x2={x + 10} y2={y + 60} className="stroke-blue-300/30" strokeWidth="2" />
                          </g>
                        );
                      })}
                    </g>

                    {/* Layer 2 - Middle Ground */}
                    <g className="animate-float-subtle-delayed opacity-30">
                      {[...Array(12)].map((_, i) => {
                        const x = i * 100 + 40;
                        const y = 240 - (i * 12);
                        return (
                          <g key={`mid-${i}`}>
                            <rect x={x} y={y} width="20" height="45" className="fill-blue-200/40" />
                            <line x1={x + 10} y1={y - 15} x2={x + 10} y2={y} className="stroke-blue-200/40" strokeWidth="2" />
                            <line x1={x + 10} y1={y + 45} x2={x + 10} y2={y + 60} className="stroke-blue-200/40" strokeWidth="2" />
                          </g>
                        );
                      })}
                    </g>

                    {/* Layer 3 - Foreground */}
                    <g className="animate-float-subtle-more-delayed opacity-40">
                      {[...Array(10)].map((_, i) => {
                        const x = i * 120 + 60;
                        const y = 200 - (i * 15);
                        return (
                          <g key={`fg-${i}`}>
                            <rect x={x} y={y} width="20" height="50" className="fill-blue-100/50" />
                            <line x1={x + 10} y1={y - 20} x2={x + 10} y2={y} className="stroke-blue-100/50" strokeWidth="2" />
                            <line x1={x + 10} y1={y + 50} x2={x + 10} y2={y + 70} className="stroke-blue-100/50" strokeWidth="2" />
                          </g>
                        );
                      })}
                    </g>
                  </g>
                </svg>
              </div>
            </div>

            <div className="relative isolate px-4 lg:px-6">
              <div className="mx-auto max-w-5xl py-12 lg:py-16">
                <div className="text-center space-y-6">
                  <h1 className="text-[3.5rem] lg:text-[5rem] font-bold tracking-tight text-white leading-none">
                    <span className="inline-block whitespace-nowrap">Compare Futures</span>
                    <span className="block mt-1 bg-gradient-to-r from-blue-200 via-white to-blue-200 bg-clip-text text-transparent font-extrabold">
                      Prop Firms
                    </span>
                  </h1>

                  <p className="mx-auto max-w-2xl text-lg text-blue-100/90 leading-relaxed">
                    Side-by-side analysis of{" "}
                    <span className="text-white font-medium">the best prop firm offerings</span>.
                    </p>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-2">
                      <Link 
                        to="/compare" 
                      className="group relative inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-xl transition-all duration-300 ease-out shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-600/30 hover:-translate-y-0.5"
                    >
                      <span className="relative flex items-center">
                        {heroCta}
                        <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                      </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Wave Divider */}
            <div className="absolute bottom-0 left-0 right-0">
              <svg className="fill-white/80 dark:fill-gray-800/50" viewBox="0 0 1920 80" xmlns="http://www.w3.org/2000/svg">
                <path d="M0,0 C480,80 960,80 1440,40 C1920,0 1920,0 1920,0 L1920,80 L0,80 L0,0 Z" />
              </svg>
            </div>
          </section>

          {/* Content Sections with Gradient Background */}
          <div className="relative overflow-hidden bg-gradient-to-b from-white via-gray-50 to-gray-100 dark:from-gray-800/50 dark:via-gray-900 dark:to-gray-900">
            {/* Background Decorative Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {/* Gradient Orbs */}
              <div className="absolute -left-1/4 top-0 w-1/2 h-1/2 bg-gradient-to-br from-brand-blue/5 via-blue-400/5 to-transparent dark:from-blue-500/10 dark:via-blue-400/5 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -right-1/4 top-1/4 w-1/2 h-1/2 bg-gradient-to-bl from-brand-green/5 via-emerald-400/5 to-transparent dark:from-emerald-500/10 dark:via-emerald-400/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
              <div className="absolute left-1/4 bottom-0 w-1/2 h-1/2 bg-gradient-to-tr from-brand-blue/5 via-blue-400/5 to-transparent dark:from-blue-500/10 dark:via-blue-400/5 rounded-full blur-3xl animate-pulse delay-2000"></div>
              
              {/* Animated Gradient Lines */}
              <div className="absolute top-0 -left-1/2 w-[200%] h-[1px] bg-gradient-to-r from-transparent via-brand-blue/10 to-transparent dark:via-blue-500/20 animate-[gradient-x_15s_linear_infinite]"></div>
              <div className="absolute bottom-0 -left-1/2 w-[200%] h-[1px] bg-gradient-to-r from-transparent via-brand-green/10 to-transparent dark:via-emerald-500/20 animate-[gradient-x_15s_linear_infinite] delay-1000"></div>
            </div>

            {/* Best Deal Banner */}
            <section className="py-6 relative z-10">
              <div className="container mx-auto px-4">
                <BestDealBanner />
              </div>
            </section>

            {/* Featured Prop Firms */}
            <section className="py-8 relative z-10">
              <div className="container mx-auto px-4">
                <FeaturedFirms limit={3} />
              </div>
            </section>

            {/* Compare All Button */}
            <section className="pb-8 relative z-10">
              <div className="container mx-auto px-4">
                <div className="flex justify-center">
                  <Link
                    to="/compare"
                    className="group inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-brand-green hover:bg-brand-darkGreen transition-all duration-300 ease-out rounded-xl shadow-lg shadow-emerald-500/10 hover:shadow-xl hover:shadow-emerald-500/20 transform hover:-translate-y-0.5"
                  >
                    {midCta}
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
              </div>
            </div>
          </section>

          {/* A/B placement test: variant B shows extra CTA banner between sections */}
          {hpVariant === 'B' && (
            <section className="py-6 relative z-10">
              <div className="container mx-auto px-4">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-6 text-center text-white shadow-lg">
                  <p className="text-lg font-semibold mb-3">Not sure which firm is right for you?</p>
                  <Link
                    to="/compare"
                    className="group inline-flex items-center px-6 py-3 text-base font-semibold text-blue-700 bg-white hover:bg-blue-50 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    Compare Side by Side
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </section>
          )}

          {/* How it works */}
            <section className="py-10 relative z-10">
              <div className="container mx-auto px-4">
                <div className="mb-8 text-center">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">How It Works</h2>
                  <p className="text-gray-500 dark:text-gray-400 mt-2">Find your firm in three steps</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                  {[
                    {
                      step: '1',
                      title: 'Pick Your Account Size',
                      body: 'Use the account size selector to filter programs that match the capital you want to trade.',
                      color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
                    },
                    {
                      step: '2',
                      title: 'Compare Prices & Rules',
                      body: 'See prices, drawdown types, profit splits, and consistency rules side by side — sorted lowest price first.',
                      color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
                    },
                    {
                      step: '3',
                      title: 'Click Through & Save',
                      body: 'If a verified discount is available, apply the code at checkout. We only show promos we\'ve confirmed.',
                      color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
                    },
                  ].map(({ step, title, body, color }) => (
                    <div key={step} className="flex flex-col items-center text-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4 ${color}`}>
                        {step}
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{body}</p>
                    </div>
                  ))}
                </div>
                <div className="flex justify-center mt-8">
                  <Link
                    to="/compare"
                    className="group inline-flex items-center px-6 py-3 text-base font-semibold text-white bg-brand-green hover:bg-brand-darkGreen transition-all duration-300 ease-out rounded-xl shadow-lg shadow-emerald-500/10 hover:shadow-xl hover:shadow-emerald-500/20 transform hover:-translate-y-0.5"
                  >
                    {howItWorksCta}
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </section>

          {/* Verification Methodology */}
            <section className="py-10 relative z-10">
              <div className="container mx-auto px-4">
                <PayoutProof />
              </div>
            </section>

          {/* Features Section */}
            <section className="py-8">
              <div className="container mx-auto px-4 relative">
                <div className="mb-6 text-center">
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">
                    Why Compare Prop Firms With Us?
                  </h2>
                </div>
              
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  <div className="group bg-white p-8 rounded-2xl border border-gray-100 shadow-lg transition-all duration-500 hover:shadow-xl hover:-translate-y-2 dark:bg-gradient-to-br dark:from-blue-900/40 dark:to-blue-800/30 dark:border-blue-800/30 dark:shadow-lg dark:shadow-blue-900/20">
                    <div className="bg-brand-lightBlue/20 p-4 rounded-2xl w-16 h-16 flex items-center justify-center mb-6 transition-transform duration-500 group-hover:scale-110 dark:bg-blue-600/30">
                      <BarChart3 className="h-8 w-8 text-brand-blue dark:text-blue-300" />
                  </div>
                    <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white group-hover:text-brand-blue dark:group-hover:text-blue-400 transition-colors duration-300">
                      Detailed Comparisons
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      Side-by-side analysis of top prop firms including profit splits, trading rules, and scaling plans.
                  </p>
                </div>
                
                  <div className="group bg-white p-8 rounded-2xl border border-gray-100 shadow-lg transition-all duration-500 hover:shadow-xl hover:-translate-y-2 dark:bg-gradient-to-br dark:from-emerald-900/40 dark:to-emerald-800/30 dark:border-emerald-800/30 dark:shadow-lg dark:shadow-emerald-900/20">
                    <div className="bg-brand-lightGreen/20 p-4 rounded-2xl w-16 h-16 flex items-center justify-center mb-6 transition-transform duration-500 group-hover:scale-110 dark:bg-emerald-600/30">
                      <DollarSign className="h-8 w-8 text-brand-green dark:text-emerald-300" />
                  </div>
                    <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white group-hover:text-brand-green dark:group-hover:text-emerald-400 transition-colors duration-300">
                      Exclusive Discounts
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      Exclusive promotional codes and discounts on prop firm challenges to help you save on your trading journey.
                  </p>
                </div>
                
                  <div className="group bg-white p-8 rounded-2xl border border-gray-100 shadow-lg transition-all duration-500 hover:shadow-xl hover:-translate-y-2 dark:bg-gradient-to-br dark:from-indigo-900/40 dark:to-indigo-800/30 dark:border-indigo-800/30 dark:shadow-lg dark:shadow-indigo-900/20">
                    <div className="bg-brand-lightBlue/20 p-4 rounded-2xl w-16 h-16 flex items-center justify-center mb-6 transition-transform duration-500 group-hover:scale-110 dark:bg-indigo-600/30">
                      <LineChart className="h-8 w-8 text-brand-blue dark:text-indigo-300" />
                  </div>
                    <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white group-hover:text-brand-blue dark:group-hover:text-indigo-400 transition-colors duration-300">
                      Freshness Tracked
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      Every listing shows when it was last verified. Stale data is flagged so you always know how current the information is.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Popular Comparisons — internal links to Best Of SEO pages */}
          <section className="py-10 relative z-10">
            <div className="container mx-auto px-4">
              <div className="mb-8 text-center">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Popular Comparisons</h2>
                <p className="text-gray-500 dark:text-gray-400 mt-2">Browse firms by account size, drawdown type, or trading style</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
                {[
                  { label: 'Best 50K Accounts', to: '/best/50k-funded-account' },
                  { label: 'Best 100K Accounts', to: '/best/100k-funded-account' },
                  { label: 'Best 150K Accounts', to: '/best/150k-funded-account' },
                  { label: 'Cheapest Firms', to: '/best/cheapest-prop-firms' },
                  { label: 'Highest Profit Splits', to: '/best/highest-profit-split-prop-firms' },
                  { label: 'EOD Drawdown Firms', to: '/best/eod-drawdown-prop-firms' },
                  { label: 'No Consistency Rule', to: '/best/no-consistency-rule-prop-firms' },
                  { label: 'No Activation Fee', to: '/best/no-activation-fee-prop-firms' },
                ].map(({ label, to }) => (
                  <Link
                    key={to}
                    to={to}
                    className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:border-brand-blue hover:text-brand-blue dark:hover:text-blue-400 transition-colors shadow-sm"
                  >
                    <TrendingUp className="h-4 w-4 shrink-0 text-brand-blue dark:text-blue-400" />
                    {label}
                  </Link>
                ))}
              </div>
              <div className="flex justify-center gap-4 mt-6">
                <Link
                  to="/tools/drawdown-simulator"
                  className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-brand-blue dark:hover:text-blue-400 transition-colors"
                >
                  <Calculator className="h-4 w-4" />
                  Drawdown Simulator
                </Link>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <Link
                  to="/methodology"
                  className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-brand-blue dark:hover:text-blue-400 transition-colors"
                >
                  <Shield className="h-4 w-4" />
                  Our Methodology
                </Link>
              </div>
            </div>
          </section>

          {/* Latest Blog Posts Section */}
          <section className="py-12 relative z-10">
            <div className="container mx-auto px-4">
              <div className="mb-8 text-center">
                <h2 className="text-2xl md:text-3xl font-bold mb-3 text-gray-900 dark:text-white">
                  Latest Trading Insights
                </h2>
                <p className="text-gray-600 dark:text-blue-100/80 max-w-2xl mx-auto">
                  Expert advice and detailed guides to help you succeed with proprietary trading firms.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Display the three most recent blog posts */}
                {[...Array(3)].map((_, index) => {
                  const post = blogPosts[index];
                  return (
                    <div key={post.id} className="bg-white rounded-lg shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md">
                      <Link to={`/blog/${post.slug}`} className="block">
                        <img 
                          src={post.featuredImage} 
                          alt={post.title} 
                          className="w-full h-48 object-cover"
                        />
                      </Link>
                      <div className="p-6">
                        <Link to={`/blog/${post.slug}`} className="block">
                          <h3 className="text-xl font-semibold mb-2 hover:text-brand-blue transition-colors">
                            {post.title}
                          </h3>
                        </Link>
                        <p className="text-gray-600 mb-4 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <Link 
                          to={`/blog/${post.slug}`} 
                          className="text-brand-blue hover:text-brand-darkBlue font-medium inline-flex items-center"
                        >
                          Read More <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-10 text-center">
                <Button asChild variant="outline" className="border-brand-blue text-brand-blue hover:bg-brand-lightBlue">
                  <Link to="/blog">
                    View All Articles
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </section>

          {/* Internal Link Module for SEO */}
          <section className="py-8 bg-gray-50 dark:bg-gray-800/50">
            <div className="container mx-auto px-4">
              <InternalLinkModule currentPath="/" />
            </div>
          </section>

          {/* Email Subscription */}
          <section className="py-8 bg-gray-50 dark:bg-gray-800/50">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-4">
                    Never Miss a Trading Opportunity
                  </h2>
                  <p className="text-gray-600 mb-4">
                    Stay informed about the latest prop firm deals, promotions, and industry news. Get alerts when firms update their rules or offer special discounts.
                  </p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-green/20 flex items-center justify-center mr-2 mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-brand-green"></div>
                      </div>
                        <span className="text-gray-700 dark:text-gray-300">Exclusive discount codes and promotions</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-green/20 flex items-center justify-center mr-2 mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-brand-green"></div>
                      </div>
                        <span className="text-gray-700 dark:text-gray-300">Alerts on rule changes and new opportunities</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-green/20 flex items-center justify-center mr-2 mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-brand-green"></div>
                      </div>
                        <span className="text-gray-700 dark:text-gray-300">Trading tips and educational content</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <EmailSubscribe />
                </div>
              </div>
            </div>
          </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Index;
